import { ModalBaseDirective } from '$core/base/_modal.component';
import { CustomValidators } from '$core/helpers/custom-validators';
import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { UserTypes, UserTypesMap } from '$core/models/user';
import { AuthenticationService } from '$core/services/auth.service';
import { SessionService } from '$core/services/session.service';
import {
  ResetPasswordData,
  ResetPasswordRequestData,
  SmsCheckData,
  UserService,
} from '$core/services/user.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { CaptchaComponent } from '$shared/components/captcha/captcha.component';
import { transition, trigger, useAnimation } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, NgForm, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fadeIn } from 'ng-animate';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { first } from 'rxjs/operators';
import { AuthParamsValues } from '$core/models/auth-modals-query-params';
import { AuthModalsService } from '$core/services/auth-modals.service';
import { LoginModalComponent } from '$core/modal/login-modal/login-modal.component';

@Component({
  selector: 'app-forgot-password-modal',
  templateUrl: './forgot-password-modal.component.html',
  animations: [
    trigger('parent', [transition(':enter', [])]),
    trigger('fadeInOut', [
      transition(':enter', useAnimation(fadeIn), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
})
export class ForgotPasswordModalComponent
  extends ModalBaseDirective
  implements OnInit
{
  public readonly UserTypes = UserTypes;

  @ViewChild('form', { static: false }) public form: NgForm;
  @ViewChild('formEl', { static: false })
  public formEl: ElementRef<HTMLFormElement>;
  @ViewChild('formCode', { static: false }) public formCode: NgForm;
  @ViewChild('formCodeEl', { static: false })
  public formCodeEl: ElementRef<HTMLFormElement>;
  public formNewPassword: FormGroup;
  public isFormNewPasswordSubmitted = false;
  @ViewChild('formNewPasswordEl', { static: false })
  public formNewPasswordEl: ElementRef<HTMLFormElement>;
  @ViewChild(CaptchaComponent, { static: false })
  public captchaComponentInstance: CaptchaComponent;

  private _phone: string;
  public get phone(): string {
    return this._phone;
  }

  public set phone(v: string) {
    this._phone = v ? v.replace(/\D/g, '') : '';
  }

  public tpi: string;
  public filial: string;
  public password: string;
  public code: string;

  public types = [UserTypes.Individual, UserTypes.Legal];
  public typesMap = UserTypesMap;
  public activeType = this.types[0];

  public isResendingCode = false;
  public isAwaitingSmsCheck = false;
  public isAwaitingNewPassword = false;
  public count: number;

  public get captchaData() {
    return this.captchaComponentInstance && this.captchaComponentInstance.data;
  }

  constructor(
    protected modalRef: BsModalRef,
    protected modalService: BsModalService,
    protected translateService: TranslateService,
    private authService: AuthenticationService,
    private sessionService: SessionService,
    private userService: UserService,
    private swalService: SwalService,
    private fb: FormBuilder,
    private authModalsService: AuthModalsService
  ) {
    super(modalRef, modalService, translateService);
  }

  public ngOnInit(): void {
    this.formNewPassword = this.fb.group({
      password: [
        null,
        [
          Validators.compose([
            Validators.required,
            CustomValidators.patternValidator(/\d/, { hasNumber: true }),
            CustomValidators.patternValidator(/[A-ZА-Я]/, {
              hasCapitalCase: true,
            }),
            CustomValidators.patternValidator(/[a-zа-я]/, {
              hasSmallCase: true,
            }),
            CustomValidators.passwordLengthValidator(),
          ]),
        ],
      ],
    });
  }

  public async resetPassword() {
    const form = this.form;
    const formEl = this.formEl;

    if (form.invalid) {
      focusFirstInvalidField(formEl);
      return;
    }

    const body = this.generateBody();
    try {
      if (this.activeType === UserTypes.Individual) {
        const result = await this.userService.passwordReset(body).toPromise();

        if (result && result.code) {
          this.code = result.code;
        }
        this.isAwaitingSmsCheck = true;
      }
      if (this.activeType === UserTypes.Legal) {
        await this.userService.passwordReset(body).toPromise();
        this.hide();
        this.modalService.onHidden.pipe(first()).subscribe(() => {
          const swal = this.swalService.swal;
          swal.title = this.translate('RESET_PASSWORD_EMAIL_SENT');
          swal.icon = 'info';
          swal.fire();
        });
      }
    } catch (error) {
      this.handleError(error, form, formEl);

      if (error instanceof HttpErrorResponse) {
        if (error.status === 422) {
          if (this.captchaComponentInstance) {
            const isCaptchaError = !!(
              error &&
              error.error &&
              error.error.errors &&
              Object.keys(error.error.errors).length === 1 &&
              error.error.errors.captcha
            );

            this.captchaComponentInstance.reload(isCaptchaError);
          }
        }
      }
    } finally {
      this.isLoading = false;
    }
  }

  public async sendNewPassword() {
    const form = this.formNewPassword;
    const formEl = this.formNewPasswordEl;

    this.isFormNewPasswordSubmitted = true;

    if (form.invalid) {
      focusFirstInvalidField(formEl);
      return;
    }

    const body: ResetPasswordData = {
      code: this.code,
      password: this.formNewPassword.value.password,
      password_confirmation: this.formNewPassword.value.password,
      phone: this.phone,
    };
    this.isLoading = true;
    try {
      const result = await this.userService
        .passwordResetConfirm(body)
        .toPromise();
      this.sessionService.session = result;
      if (result.token) {
        this.authService.update(result.token, true);
      }
      this.hide();
      this.modalService.onHidden.pipe(first()).subscribe(() => {
        const swal = this.swalService.swal;
        swal.title = this.translate('NEW_PASSWORD_SET_SUCCESSFULLY');
        swal.icon = 'success';
        swal.fire();
      });
    } catch (error) {
      this.handleError(error as HttpErrorResponse, form, formEl);
    } finally {
      this.isLoading = false;
    }
  }

  public async sendCode() {
    const form = this.formCode;
    const formEl = this.formCodeEl;

    if (form.invalid) {
      focusFirstInvalidField(formEl);
      return;
    }

    const body: SmsCheckData = {
      phone: this.phone,
      code: this.code,
    };
    this.isLoading = true;
    try {
      const result = await this.userService
        .passwordResetCheck(body)
        .toPromise();
      this.isAwaitingSmsCheck = false;
      this.isAwaitingNewPassword = true;
      // if (result.status === 'ok') {
      // }
    } catch (error) {
      this.handleError(error as HttpErrorResponse, form, formEl);
    } finally {
      this.isLoading = false;
    }
  }

  public async resendCode() {
    const form = this.formCode;
    const formEl = this.formCodeEl;

    this.isResendingCode = true;
    try {
      const result = await this.userService
        .passwordReset(this.generateBody())
        .toPromise();
      if (result.code) {
        this.code = result.code;
      }
      this.isAwaitingSmsCheck = true;
    } catch (error) {
      this.handleError(error as HttpErrorResponse, form, formEl);
    } finally {
      this.isResendingCode = false;
    }
  }

  private generateBody() {
    let body;
    if (this.activeType === UserTypes.Individual) {
      body = {
        login: this.phone,
        type: this.activeType,
        captcha: this.form.value.captcha,
        key: this.captchaData.key,
      } as ResetPasswordRequestData;
    }
    if (this.activeType === UserTypes.Legal) {
      body = {
        login: this.tpi,
        filial: this.filial || null,
        type: this.activeType,
      } as ResetPasswordRequestData;
    }
    return body;
  }

  public toLogin() {
    this.hide();
    this.authModalsService.show(
      LoginModalComponent,
      this.activeType,
      AuthParamsValues.SIGNIN
    );
  }

  public changeTab(type: UserTypes) {
    this.authModalsService.setUserTypeQueryParam(type);
    this.activeType = type;
  }

  public hide(): void {
    this.authModalsService.hide();
    super.hide();
  }
}
